"use client";

//region Imports
import React, {useEffect, useRef, useState} from "react";

import type {HeaderElement, FooterElement} from "../";
import {ToolbarTop, ToolbarBottom, useOpenComponent, useDialogController} from "../";
import {replaceLinks} from "../../functions";
//endregion Imports

//region Types & Interfaces
interface ConfirmProps {
  dialogOptions?: any;
  title?: string;
  text?: string;
  yesLabel?: string;
  noLabel?: string;
  onYes: () => void | Promise<void>
  onNo: () => void | Promise<void>
}
//endregion Types & Interfaces

export const Confirm: React.FC<ConfirmProps> = ({
                                                  dialogOptions,
                                                  title = "Confirm",
                                                  text = "Confirm?",
                                                  yesLabel = "Yes",
                                                  noLabel = "No",
                                                  onYes,
                                                  onNo
                                                }) => {
  //region Variables
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");
  const openComponent = useOpenComponent();
  //endregion Variables

  //region Methods & Event Handlers
  const {
    closeDialog,
    setOnClose
  } = useDialogController();

  const openPrivacyPolicy = async () => {
    openComponent("privacyPolicy");
  };

  const openTermsOfUse = async () => {
    openComponent("termsOfUse");
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    // console.log({distanceFromTop, distanceFromBottom});

    const SHOW_THRESHOLD = 2;
    const HIDE_THRESHOLD = 15;
    const TOP_HIDE_THRESHOLD = 15;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const onKeyDown = async (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onYes();
  }

  const _onYes = async () => {
    await closeDialog(true);
    await onYes?.();
  };

  const _onNo = async () => {
    await onNo?.();
  };
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setOnClose(async () => {
      await _onNo();
    });
  }, [setOnClose, _onNo]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: title,
      style: "text-md md:text-lg text-white font-bold tracking-wider",
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: async () => await closeDialog(true)
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: yesLabel.toLowerCase(),
      type: "gradientButton",
      label: yesLabel,
      style: `btn-blue transition duration-200 hover:text-generic-hover hover:scale-105'}`,
      onClick: _onYes
    },
    {
      name: noLabel.toLowerCase(),
      type: "gradientButton",
      label: noLabel,
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: async () => await closeDialog(true)
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 p-4 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      <p
        className="text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed">
        {replaceLinks(text, true, {
          openPrivacyPolicy,
          openTermsOfUse
        })}
      </p>
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
        absolute bottom-0 left-0 right-0 z-50
        transition-transform duration-300 ease-in-out
        ${showFooter ? "translate-y-0" : "translate-y-full"}
      `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onWheel={(e) => e.stopPropagation()}>
      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
